import i18n from 'i18next';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import useStyles from './MissionDetails.styles';
import { MissionExact } from '../../models/Mission';
import { TreeView } from '../../../common/components/layout';
import MissionMap from '../map/MissionMap';
import { getCenteredEntities, getEntitiesWithVisibility } from '../../../map/utils/mapFunctions';
import useAgent from '../../../common/hooks/useAgent';
import missionAgent from '../../agents/missionAgent';
import { Loader } from '../../../common/components/loaders';
import PipelineCenterlineTreeView from './treeViews/PipelineCenterlineTreeView';
import { PipelineCenterlineExact } from '../../models/PipelineCenterline';
import { kmToMiles, kmToNauticalMiles } from '../../../common/utils/converter';

const MissionDetails = ({ mission, pipelineCenterlines }) => {
  const classes = useStyles();

  const { setFieldValue } = useFormikContext();

  const [routes, setRoutes] = useState(mission && mission.routes);
  const [workzones, setWorkzones] = useState(mission && mission.workzones);
  const [jurisdictions, setJurisdictions] = useState(mission && mission.jurisdictions);
  const [urbanAreas, setUrbanAreas] = useState(mission && mission.urbanAreas);
  const [obstacles, setObstacles] = useState(mission && mission.obstacles);
  const [centerlines, setCenterlines] = useState(pipelineCenterlines || (mission && mission.centerlines) || []);
  const [airports, setAirports] = useState(mission && mission.airports);
  const [centeredGeometries, setCenteredGeometries] = useState();

  const [fetchMissionObstacles, missionObstacles, isFetchingMissionObstacles] = useAgent(missionAgent.fetchMissionObstacles);
  const [fetchMissionAirports, missionAirports, isFetchingAirports] = useAgent(missionAgent.fetchMissionAirports);

  useEffect(() => {
    if (!obstacles || !obstacles.length) fetchMissionObstacles(mission);
  }, [fetchMissionObstacles, mission, obstacles]);

  useEffect(() => {
    if (missionObstacles && missionObstacles.length) {
      setFieldValue('obstacles', missionObstacles);
      setObstacles(missionObstacles);
    }
  }, [missionObstacles, setFieldValue]);

  useEffect(() => {
    if (!airports || !airports.length) fetchMissionAirports(mission);
  }, [fetchMissionAirports, mission, airports]);

  useEffect(() => {
    if (missionAirports && missionAirports.length) {
      setFieldValue('airports', missionAirports);
      setAirports(missionAirports);
    }
  }, [missionAirports, setFieldValue]);

  useEffect(() => {
    if (pipelineCenterlines && pipelineCenterlines.length) {
      setFieldValue('centerlines', pipelineCenterlines);
      setCenterlines(pipelineCenterlines);
    }
  }, [pipelineCenterlines, setFieldValue]);

  // TODO: Move every TreeView into its own file, like PipelineCenterline
  const handleRoutesCenterClick = useCallback(
    (item) => {
      setCenteredGeometries(getCenteredEntities(mission.routes, item.id));
    },
    [mission.routes]
  );

  const handleRoutesVisibilityClick = useCallback((item, isToggled) => {
    setRoutes((oldRoutes) => getEntitiesWithVisibility(oldRoutes, item.id, isToggled));
  }, []);

  const { current: routeItemActions } = useRef([
    { id: 'route-center', icons: { default: 'gps_fixed' }, onClick: handleRoutesCenterClick },
    {
      id: 'route-visibility',
      icons: { default: 'visibility', toggled: 'visibility_off' },
      onClick: handleRoutesVisibilityClick,
    },
  ]);
  const { current: routeItems } = useRef([
    {
      id: 'root',
      label: `${i18n.t('mission.details.routes')}: ${mission.routeCount}`,
      actions: routeItemActions,
      children: mission.routes.map((route, index) => ({
        id: index,
        label: `${route.name}: ${kmToMiles(route.lengthInKilometers).toFixed(2)} mi 
        (${kmToNauticalMiles(route.lengthInKilometers).toFixed(2)} nm)`,
        actions: routeItemActions,
      })),
    },
  ]);

  const handleWorkzonesCenterClick = useCallback(
    (item) => {
      setCenteredGeometries(getCenteredEntities(mission.workzones, item.id));
    },
    [mission.workzones]
  );

  const handleWorkzonesVisibilityClick = useCallback((item, isToggled) => {
    setWorkzones((oldWorkzones) => getEntitiesWithVisibility(oldWorkzones, item.id, isToggled));
  }, []);

  const { current: workzoneItemActions } = useRef([
    { id: 'workzone-center', icons: { default: 'gps_fixed' }, onClick: handleWorkzonesCenterClick },
    {
      id: 'workzone-visibility',
      icons: { default: 'visibility', toggled: 'visibility_off' },
      onClick: handleWorkzonesVisibilityClick,
    },
  ]);
  const { current: workzoneItems } = useRef([
    {
      id: 'root',
      label: `${i18n.t('mission.details.workzones')}: ${mission.workzoneCount}`,
      actions: workzoneItemActions,
      children: mission.workzones.map((workzone, index) => ({
        id: index,
        label: `Workzone #${index + 1}`,
        actions: workzoneItemActions,
      })),
    },
  ]);

  const handleUrbanAreasCenterClick = useCallback(
    (item) => {
      setCenteredGeometries(getCenteredEntities(mission.urbanAreas, item.id));
    },
    [mission.urbanAreas]
  );

  const handleUrbanAreasVisibilityClick = useCallback((item, isToggled) => {
    setUrbanAreas((oldUrbanAreas) => getEntitiesWithVisibility(oldUrbanAreas, item.id, isToggled));
  }, []);

  const { current: urbanAreaItemActions } = useRef([
    { id: 'urbanArea-center', icons: { default: 'gps_fixed' }, onClick: handleUrbanAreasCenterClick },
    {
      id: 'urbanArea-visibility',
      icons: { default: 'visibility', toggled: 'visibility_off' },
      onClick: handleUrbanAreasVisibilityClick,
    },
  ]);
  const { current: urbanAreaItems } = useRef([
    {
      id: 'root',
      label: `${i18n.t('mission.details.urbanAreas')}: ${mission.urbanAreas.length}`,
      actions: urbanAreaItemActions,
      children: mission.urbanAreas.map((_, index) => ({
        id: index,
        label: _.name ? _.name : `${i18n.t('mission.details.urbanArea')} #${index + 1}`,
        actions: urbanAreaItemActions,
      })),
    },
  ]);
  const handleJurisdictionsCenterClick = useCallback(
    (item) => {
      setCenteredGeometries(getCenteredEntities(mission.jurisdictions, item.id));
    },
    [mission.jurisdictions]
  );
  const handleJurisdictionsVisibilityClick = useCallback((item, isToggled) => {
    setJurisdictions((oldJurisdictions) => getEntitiesWithVisibility(oldJurisdictions, item.id, isToggled));
  }, []);

  const { current: jurisdictionItemActions } = useRef([
    { id: 'jurisdiction-center', icons: { default: 'gps_fixed' }, onClick: handleJurisdictionsCenterClick },
    {
      id: 'jurisdiction-visibility',
      icons: { default: 'visibility', toggled: 'visibility_off' },
      onClick: handleJurisdictionsVisibilityClick,
    },
  ]);
  const { current: jurisdictionItems } = useRef([
    {
      id: 'root',
      label: `${i18n.t('mission.details.jurisdictions')}: ${mission.jurisdictions.length}`,
      actions: jurisdictionItemActions,
      children: mission.jurisdictions.map((jurisdiction, index) => ({
        id: index,
        label: jurisdiction.zoneId,
        actions: jurisdictionItemActions,
      })),
    },
  ]);

  const handleObstaclesCenterClick = useCallback(
    (item) => {
      setCenteredGeometries(getCenteredEntities(obstacles || [], item.id));
    },
    [obstacles]
  );

  const handleObstaclesVisibilityClick = useCallback((item, isToggled) => {
    setObstacles((oldObstacles) => getEntitiesWithVisibility(oldObstacles, item.id, isToggled));
  }, []);

  const obstaclesItemActions = useMemo(
    () => [
      { id: 'obstacle-center', icons: { default: 'gps_fixed' }, onClick: handleObstaclesCenterClick },
      {
        id: 'obstacle-visibility',
        icons: { default: 'visibility', toggled: 'visibility_off' },
        onClick: handleObstaclesVisibilityClick,
      },
    ],
    [handleObstaclesCenterClick, handleObstaclesVisibilityClick]
  );

  const obstacleItems = useMemo(
    () => [
      {
        id: 'root',
        label: `${i18n.t('mission.details.obstacles')}: ${obstacles && obstacles.length}`,
        actions: obstaclesItemActions,
      },
    ],
    [obstacles, obstaclesItemActions]
  );

  const handleAirportsCenterClick = useCallback(
    (item) => {
      setCenteredGeometries(getCenteredEntities(airports || [], item.id));
    },
    [airports]
  );

  const handleAirportsVisibilityClick = useCallback((item, isToggled) => {
    setAirports((oldAirports) => getEntitiesWithVisibility(oldAirports, item.id, isToggled));
  }, []);

  const airportsItemActions = useMemo(
    () => [
      { id: 'airport-center', icons: { default: 'gps_fixed' }, onClick: handleAirportsCenterClick },
      {
        id: 'airport-visibility',
        icons: { default: 'visibility', toggled: 'visibility_off' },
        onClick: handleAirportsVisibilityClick,
      },
    ],
    [handleAirportsCenterClick, handleAirportsVisibilityClick]
  );

  const airportItem = useMemo(
    () => [
      {
        id: 'root',
        label: `${i18n.t('mission.details.airports')}: ${airports && airports.length}`,
        actions: airportsItemActions,
        children:
          airports &&
          airports.map((airport, index) => ({
            id: index,
            label: airport.name,
            actions: airportsItemActions,
          })),
      },
    ],
    [airports, airportsItemActions]
  );

  const progressStyle = { height: '20px', width: '20px' };

  return (
    <div className={classes.root}>
      <div className={classes.details}>
        <div className={classes.title}>{i18n.t('mission.missionDetails')}</div>
        <div className={classes.content}>
          <div>{`${i18n.t('mission.details.length')}: ${kmToMiles(mission.totalRouteLengthInKilometers).toFixed(2)} mi  
          (${kmToNauticalMiles(mission.totalRouteLengthInKilometers).toFixed(2)} nm)`}</div>

          <TreeView id="routes" items={routeItems} />
          {((pipelineCenterlines && pipelineCenterlines.length !== 0) || (mission.centerlines && mission.centerlines.length !== 0)) && (
            <PipelineCenterlineTreeView
              centerlines={pipelineCenterlines || mission.centerlines}
              setCenterlines={setCenterlines}
              setCenteredGeometries={setCenteredGeometries}
            />
          )}
          {mission.workzones.length !== 0 && <TreeView id="workzones" items={workzoneItems} />}
          {mission.urbanAreas.length !== 0 && <TreeView id="urbanAreas" items={urbanAreaItems} />}
          <TreeView id="jurisdictions" items={jurisdictionItems} />
          {isFetchingAirports ? (
            <div className={classes.loader}>
              <div>
                <Loader circularProgressStyle={progressStyle} />
              </div>
              <div className={classes.loaderInformation}>{`${i18n.t('mission.details.loadingAirports')}...`}</div>
            </div>
          ) : (
            airports && airports.length !== 0 && <TreeView id="airports" items={airportItem} />
          )}
          {isFetchingMissionObstacles ? (
            <div className={classes.loader}>
              <div>
                <Loader circularProgressStyle={progressStyle} />
              </div>
              <div className={classes.loaderInformation}>{`${i18n.t('mission.details.loadingObstacles')}...`}</div>
            </div>
          ) : (
            obstacles && obstacles.length !== 0 && <TreeView id="obstacles" items={obstacleItems} />
          )}
        </div>
      </div>

      <MissionMap
        routes={routes}
        workzones={workzones}
        urbanAreas={urbanAreas}
        jurisdictions={jurisdictions}
        obstacles={obstacles || []}
        airports={airports || []}
        pipelineCenterlines={centerlines || []}
        centeredGeometries={centeredGeometries}
      />
    </div>
  );
};

MissionDetails.propTypes = {
  mission: MissionExact,
  pipelineCenterlines: PropTypes.arrayOf(PipelineCenterlineExact),
};

MissionDetails.defaultProps = {
  mission: undefined,
  pipelineCenterlines: undefined,
};

export default MissionDetails;
